/**
 * Cluster Service
 *
 * Frontend service for managing location clusters.
 * Clusters are organization-scoped custom groupings of regions, countries, and/or locations.
 * Uses Firebase client SDK directly following the esimService.ts pattern.
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import type {
  Cluster,
  ClusterItem,
  ClusterItemType,
  Region,
  Country,
  Location,
} from '@simcheck/shared-types';

// Helper to convert Firestore Timestamp or ISO string to Date
function toDate(value: any): Date {
  if (!value) return new Date();
  if (value.toDate && typeof value.toDate === 'function') {
    return value.toDate();
  }
  if (typeof value === 'string') {
    return new Date(value);
  }
  if (value instanceof Date) {
    return value;
  }
  return new Date();
}

// ============================================================================
// TYPES
// ============================================================================

export interface CreateClusterData {
  name: string;
  description?: string;
  items: ClusterItem[];
  tags?: string[];
}

export interface UpdateClusterData {
  name?: string;
  description?: string;
  items?: ClusterItem[];
  tags?: string[];
}

export interface ResolvedLocation {
  locationId: string;
  locationName: string;
  countryId: string;
  countryName: string;
  regionId: string;
  regionName: string;
  modemCount: number;
}

// ============================================================================
// CLUSTER CRUD
// ============================================================================

/**
 * Get all clusters for an organization
 */
export async function getClustersForOrg(organizationId: string): Promise<Cluster[]> {
  try {
    const q = query(
      collection(db, 'clusters'),
      where('organizationId', '==', organizationId),
      orderBy('name', 'asc')
    );

    const snapshot = await getDocs(q);
    const clusters: Cluster[] = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: toDate(doc.data().createdAt),
      updatedAt: toDate(doc.data().updatedAt),
    })) as Cluster[];

    return clusters;
  } catch (error: any) {
    console.error('❌ Error fetching clusters:', error);
    throw new Error(`Failed to fetch clusters: ${error.message}`);
  }
}

/**
 * Get a single cluster by ID
 */
export async function getCluster(clusterId: string): Promise<Cluster | null> {
  try {
    const docRef = doc(db, 'clusters', clusterId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      return null;
    }

    const data = docSnap.data();
    return {
      id: docSnap.id,
      ...data,
      createdAt: toDate(data.createdAt),
      updatedAt: toDate(data.updatedAt),
    } as Cluster;
  } catch (error: any) {
    console.error('❌ Error fetching cluster:', error);
    throw new Error(`Failed to fetch cluster: ${error.message}`);
  }
}

/**
 * Create a new cluster
 */
export async function createCluster(
  organizationId: string,
  userId: string,
  data: CreateClusterData
): Promise<Cluster> {
  try {
    // Validate required fields
    if (!data.name || !data.items || data.items.length === 0) {
      throw new Error('Name and at least one item are required');
    }

    // Calculate initial stats (will be recalculated on backend)
    const now = Timestamp.now();

    const clusterData = {
      name: data.name,
      description: data.description || null,
      organizationId,
      items: data.items,
      tags: data.tags || [],
      locationCount: 0,  // Will be calculated by backend
      modemCount: 0,     // Will be calculated by backend
      createdBy: userId,
      createdAt: now,
      updatedAt: now,
    };

    const docRef = await addDoc(collection(db, 'clusters'), clusterData);

    return {
      id: docRef.id,
      ...clusterData,
      createdAt: now.toDate(),
      updatedAt: now.toDate(),
    } as Cluster;
  } catch (error: any) {
    console.error('❌ Error creating cluster:', error);
    throw new Error(`Failed to create cluster: ${error.message}`);
  }
}

/**
 * Update a cluster
 */
export async function updateCluster(
  clusterId: string,
  data: UpdateClusterData
): Promise<void> {
  try {
    const updateData: any = {
      updatedAt: Timestamp.now(),
    };

    if (data.name !== undefined) updateData.name = data.name;
    if (data.description !== undefined) updateData.description = data.description;
    if (data.items !== undefined) {
      if (data.items.length === 0) {
        throw new Error('Cluster must have at least one item');
      }
      updateData.items = data.items;
    }
    if (data.tags !== undefined) updateData.tags = data.tags;

    await updateDoc(doc(db, 'clusters', clusterId), updateData);
  } catch (error: any) {
    console.error('❌ Error updating cluster:', error);
    throw new Error(`Failed to update cluster: ${error.message}`);
  }
}

/**
 * Delete a cluster
 */
export async function deleteCluster(clusterId: string): Promise<void> {
  try {
    // Check if any test configurations are using this cluster
    const configsQuery = query(
      collection(db, 'test_configurations'),
      where('clusterId', '==', clusterId)
    );
    const configsSnapshot = await getDocs(configsQuery);

    if (!configsSnapshot.empty) {
      throw new Error('Cannot delete cluster that is used in test configurations');
    }

    await deleteDoc(doc(db, 'clusters', clusterId));
  } catch (error: any) {
    console.error('❌ Error deleting cluster:', error);
    throw new Error(`Failed to delete cluster: ${error.message}`);
  }
}

// ============================================================================
// CLUSTER RESOLUTION
// ============================================================================

/**
 * Resolve cluster items to location IDs (for local preview)
 * Note: For execution, use the backend API which has access to all location data
 */
export async function resolveClusterToLocationIds(
  items: ClusterItem[],
  regions: Region[],
  countries: Country[],
  locations: Location[]
): Promise<string[]> {
  const locationIds = new Set<string>();

  for (const item of items) {
    switch (item.type) {
      case 'location':
        // Direct location reference - check it exists
        const directLoc = locations.find(l => l.id === item.id);
        if (directLoc) {
          locationIds.add(item.id);
        }
        break;

      case 'country':
        // Get all locations in this country (include all statuses, not just 'active')
        const countryLocs = locations.filter(l => l.countryId === item.id);
        countryLocs.forEach(l => locationIds.add(l.id));
        break;

      case 'region':
        // Get all locations in this region (include all statuses, not just 'active')
        const regionLocs = locations.filter(l => l.regionId === item.id);
        regionLocs.forEach(l => locationIds.add(l.id));
        break;
    }
  }

  return Array.from(locationIds);
}

/**
 * Resolve cluster items to full location details (for display)
 */
export async function resolveClusterToLocations(
  items: ClusterItem[],
  regions: Region[],
  countries: Country[],
  locations: Location[]
): Promise<ResolvedLocation[]> {
  const locationIds = await resolveClusterToLocationIds(items, regions, countries, locations);

  return locationIds.map(locationId => {
    const location = locations.find(l => l.id === locationId);
    const country = location ? countries.find(c => c.id === location.countryId) : null;
    const region = location ? regions.find(r => r.id === location.regionId) : null;

    return {
      locationId,
      locationName: location?.displayName || location?.name || 'Unknown',
      countryId: location?.countryId || '',
      countryName: country?.name || 'Unknown',
      regionId: location?.regionId || '',
      regionName: region?.name || 'Unknown',
      modemCount: location?.modemCount || 0,
    };
  });
}

/**
 * Calculate stats for a set of cluster items (for preview before saving)
 */
export function calculateClusterStats(
  items: ClusterItem[],
  regions: Region[],
  countries: Country[],
  locations: Location[]
): { locationCount: number; modemCount: number } {
  const locationIds = new Set<string>();
  let modemCount = 0;

  for (const item of items) {
    switch (item.type) {
      case 'location':
        const loc = locations.find(l => l.id === item.id);
        if (loc) {
          locationIds.add(item.id);
          modemCount += loc.modemCount || 0;
        }
        break;

      case 'country':
        locations
          .filter(l => l.countryId === item.id)
          .forEach(l => {
            locationIds.add(l.id);
            modemCount += l.modemCount || 0;
          });
        break;

      case 'region':
        locations
          .filter(l => l.regionId === item.id)
          .forEach(l => {
            locationIds.add(l.id);
            modemCount += l.modemCount || 0;
          });
        break;
    }
  }

  return {
    locationCount: locationIds.size,
    modemCount,
  };
}

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Build display name for a cluster item
 */
export function getClusterItemDisplayName(
  item: ClusterItem,
  regions: Region[],
  countries: Country[],
  locations: Location[]
): string {
  switch (item.type) {
    case 'region':
      const region = regions.find(r => r.id === item.id);
      return region?.name || item.displayName || 'Unknown Region';

    case 'country':
      const country = countries.find(c => c.id === item.id);
      return country?.name || item.displayName || 'Unknown Country';

    case 'location':
      const location = locations.find(l => l.id === item.id);
      return location?.displayName || location?.name || item.displayName || 'Unknown Location';

    default:
      return item.displayName || 'Unknown';
  }
}

/**
 * Get icon/badge info for a cluster item type
 */
export function getClusterItemTypeInfo(type: ClusterItemType): {
  label: string;
  color: string;
} {
  switch (type) {
    case 'region':
      return { label: 'Region', color: 'bg-accent-blue' };
    case 'country':
      return { label: 'Country', color: 'bg-accent-purple' };
    case 'location':
      return { label: 'Location', color: 'bg-accent-green' };
    default:
      return { label: 'Unknown', color: 'bg-muted' };
  }
}

/**
 * Get all clusters that include a specific location
 */
export function getClustersContainingLocation(
  locationId: string,
  clusters: Cluster[],
  location: Location
): Cluster[] {
  return clusters.filter(cluster =>
    cluster.items.some(item => {
      if (item.type === 'location') {
        return item.id === locationId;
      }
      if (item.type === 'country') {
        return item.id === location.countryId;
      }
      if (item.type === 'region') {
        return item.id === location.regionId;
      }
      return false;
    })
  );
}
